import React from "react";
import {
  useCreateEvent,
  useCreateMilestone,
  useListCreation,
  useMoveListElement,
  useTaskCreation,
  useTaskStatusesRetrieval,
} from "@planzone/view-models";
import {
  Identifiable,
  List,
  list,
  milestone,
  Project,
  ProjectElementType,
  RankedElement,
  Status,
  task,
} from "@planzone/dto";
import { ElementTypes } from "@common/elementCreationForm/types";
import { Event, events } from "@planzone/events";
import { useModal } from "@root/client/modal";
import { UseCases } from "@planzone/core";

function useRequestState(type: ElementTypes) {
  const { isRequestPending: isTaskCreationRequestPending } = useTaskCreation();
  const { isRequestPending: isMilestoneCreationRequestPending } =
    useCreateMilestone();
  const { isRequestPending: isListCreationRequestPending } = useListCreation();
  const { isRequestPending: isEventCreationRequestPending } = useCreateEvent();

  return (
    isTaskCreationRequestPending ||
    isListCreationRequestPending ||
    isEventCreationRequestPending ||
    isMilestoneCreationRequestPending
  );
}

function useTaskStatus(project: Identifiable<Project>) {
  const {
    isRequestPending: isTaskStatusesRetrievalRequestPending,
    isRequestSuccess: isTaskStatusesRetrievalRequestSuccess,
    isRequestFailure: isTaskStatusesRetrievalRequestFailure,
    statuses,
    retrieveTaskStatuses,
  } = useTaskStatusesRetrieval(project);

  React.useEffect(() => {
    !isTaskStatusesRetrievalRequestPending &&
      !isTaskStatusesRetrievalRequestSuccess &&
      !isTaskStatusesRetrievalRequestFailure &&
      retrieveTaskStatuses(project);
  }, [
    project,
    isTaskStatusesRetrievalRequestPending,
    isTaskStatusesRetrievalRequestSuccess,
    isTaskStatusesRetrievalRequestFailure,
    retrieveTaskStatuses,
  ]);

  return statuses.find(
    ({ percentComplete, basic }) => basic && percentComplete === 0,
  );
}

function useCreateElement(
  type: ElementTypes,
  project: Identifiable<Project>,
  parentList?: Identifiable<List>,
  taskStatus?: Identifiable<Status>,
) {
  const { pushView } = useModal();
  const { createTask } = useTaskCreation();
  const { createMilestone } = useCreateMilestone();
  const { createList } = useListCreation();
  const now = new Date();

  return (
    name: string,
    range?: {
      min: RankedElement<any> | null;
      max: RankedElement<any> | null;
    },
  ) =>
    type === ElementTypes.TASK
      ? parentList &&
        taskStatus &&
        createTask({
          list: parentList,
          task: { ...task, name, status: taskStatus, activityKeyRank: -1 },
          project,
          range,
        })
      : type === ElementTypes.LIST
        ? createList({
            list: { ...list, name, parentList: parentList },
            project,
            range,
          })
        : type === ElementTypes.MILESTONE
          ? parentList &&
            taskStatus &&
            createMilestone({
              list: parentList,
              milestone: { ...milestone, name },
              project,
              range,
            })
          : type === ElementTypes.EVENT
            ? parentList &&
              pushView({
                key: UseCases.createEvent,
                data: {
                  project,
                  list: parentList!,
                  range,
                  initialFormData: { name },
                },
              })
            : null;
}

function useCleanUp(
  parentList?: Identifiable<List>,
  range?: {
    min: RankedElement<any> | null;
    max: RankedElement<any> | null;
  },
) {
  const { moveListElement } = useMoveListElement();

  return (type: ElementTypes, done?: () => void) => (event: Event) => {
    if (
      event.type === events.createTaskSucceeded &&
      type === ElementTypes.TASK
    ) {
      typeof event.data.range === "undefined" &&
        !!range &&
        moveListElement({
          currentParentList: event.data.list,
          projectElementType: ProjectElementType.TASK,
          projectElementId: event.data.task.uuid,
          nextParentList: event.data.list,
          range,
          alignDates: false,
        });
      done && done();
    } else if (
      event.type === events.createEventSucceeded &&
      type === ElementTypes.EVENT
    ) {
      typeof event.data.range === "undefined" &&
        !!range &&
        moveListElement({
          currentParentList: event.data.list,
          projectElementType: ProjectElementType.EVENT,
          projectElementId: event.data.event.uuid,
          nextParentList: event.data.list,
          range,
          alignDates: false,
        });
      done && done();
    } else if (
      event.type === events.createMilestoneSucceeded &&
      type === ElementTypes.MILESTONE
    ) {
      typeof event.data.range === "undefined" &&
        !!range &&
        moveListElement({
          currentParentList: event.data.list,
          projectElementType: ProjectElementType.MILESTONE,
          projectElementId: event.data.milestone.uuid,
          nextParentList: event.data.list,
          range,
          alignDates: false,
        });
      done && done();
    } else if (
      event.type === events.createListSucceeded &&
      type === ElementTypes.LIST
    ) {
      typeof event.data.range === "undefined" &&
        !!range &&
        moveListElement({
          currentParentList: parentList ||
            event.data.list.parentList || { ...list, uuid: "" },
          projectElementType: ProjectElementType.LIST,
          projectElementId: event.data.list.uuid,
          nextParentList: parentList ||
            event.data.list.parentList || { ...list, uuid: "" },
          range,
          alignDates: false,
        });
      done && done();
    }
  };
}

export { useCreateElement, useRequestState, useTaskStatus, useCleanUp };
